import type { Branding } from "../../shared/types/commonTypes";
import type { FC } from "react";
import type { GetNotificationSettingsResponse } from "../../shared/payloads/notificationSettingsPayloads";

import { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { v4 } from "uuid";
import DOMPurify from "dompurify";
import NotificationPreview from "../../components/NotificationPreview";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { getMinVisitors } from "../../utils/helpers/getMinVisitors";
import { getAppLanguage } from "../../utils/languageHelper";

interface NotificationTablePreviewProps {
  data: GetNotificationSettingsResponse;
  branding: Branding;
}

export const NotificationTablePreview: FC<NotificationTablePreviewProps> = ({
  data,
  branding,
}) => {
  const [notificationHtml, setNotificationHtml] = useState<string>("");

  const notificationStyleId = v4();
  const { t, i18n } = useTranslation();

  const individualNotificationText = data?.text
    ? EditorState.createWithContent(
        ContentState.createFromBlockArray(
          htmlToDraft(data.text).contentBlocks,
          htmlToDraft(data.text).entityMap,
        ),
      )
    : EditorState.createEmpty();

  const previewNotificationLanguage = getAppLanguage(i18n.language);

  const notificationParams = {
    lastOrders: [
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      data?.text ?? t("campaign.notifications.lastOrders.previewText2") ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    lastRegistrations: [
      t("campaign.notifications.lastRegistrations.previewText1") ?? "",
      data?.text ?? t("campaign.notifications.lastOrders.previewText2") ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    actualVisitors: [
      undefined,
      data?.text
        ? data.text
        : t(
            "campaign.notifications.settings.displayRules.actualVisitors.visitors",
          ) ?? "",
      data?.minVisitors !== undefined ? data?.minVisitors : 10,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    visitorSummary: [
      undefined,
      data?.text || t("campaign.notifications.visitorSummary.previewText1"),
      undefined,
      previewNotificationLanguage,
      data ? getMinVisitors(data) : 20,
      data?.maxLastDays ? data?.maxLastDays : 7,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    registrationSummary: [
      undefined,
      t("campaign.notifications.visitorSummary.previewText2") ?? "",
      undefined,
      previewNotificationLanguage,
      3,
      10,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    orderSummary: [
      undefined,
      t("campaign.notifications.visitorSummary.previewText2") ?? "",
      undefined,
      previewNotificationLanguage,
      2,
      10,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    heureka: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      t("campaign.notifications.heureka.previewText1") ?? "",
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    zboziCompanyRating: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      t("campaign.notifications.zbozi.previewText1") ?? "",
      80,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    zboziProductRating: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      t("campaign.notifications.zbozi.previewText1") ?? "",
      (data?.minReviewStars !== undefined ? data?.minReviewStars : 4) * 20,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    spolehlivaRecenzeShopRating: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      t("campaign.notifications.zbozi.previewText1") ?? "",
      4,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    spolehlivaRecenzeProductRating: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      t("campaign.notifications.zbozi.previewText1") ?? "",
      4,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    individual: [
      undefined,
      draftToHtml(
        convertToRaw(individualNotificationText.getCurrentContent()),
      ) ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      data?.addLink ?? true,
      data?.linkText ?? "",
      data?.linkAsButton !== undefined ? data?.linkAsButton : true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    shoptetLastOrders: [
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      t("campaign.notifications.shoptetLastOrders.screwdriver") ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    eshopRychleLastOrders: [
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      t("campaign.notifications.eshopRychleLastOrders.screwdriver") ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    shoptetCouponCode: [
      undefined,
      undefined,
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      data?.discountCouponName ||
        t("campaign.notifications.shoptetCouponCode.sale10"),
      data?.discountCouponValue ?? 10,
      data?.discountCouponType ?? "%",
      data?.discountCouponAppliableTo ||
        t("campaign.notifications.shoptetCouponCode.wholeSale"),
    ],
    shopifyLastOrders: [
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      t("campaign.notifications.shopifyLastOrders.screwdriver") ?? "",
      undefined,
      previewNotificationLanguage,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ],
    googleReviews: [
      undefined,
      t("campaign.notifications.googleReviews.previewText1") ?? "",
      undefined,
      previewNotificationLanguage,
      4,
      5,
    ],
    trustpilotReviews: [
      undefined,
      t("campaign.notifications.trustPilot.previewText1") ?? "",
      undefined,
      previewNotificationLanguage,
      4,
      5,
    ],
  };

  const params = notificationParams[data?.type] || [];

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 0,
            },
          },
          settings: {
            notificationVisualSettings: {
              backgroundColor:
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: data?.notificationVisualSettings?.icon ?? "",
              roundness:
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        data?.type,
        ...(params as any),
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [branding]);

  return <NotificationPreview notificationHtml={notificationHtml} />;
};
